import {
  ActionLabel,
  APPROVAL_STATUSES,
  isFromScratch,
  getPlaceholder,
  getPlaceholderForConversionReportType,
  getPlaceholderForCustomReportType,
  getPlaceholderForPFTrans,
  getPlaceholderForPFFinal,
  getStatus,
  getStatusForConversionReportType,
  getStatusForCustomReportType,
  getStatusForPFTrans,
  getStatusForPFFinal,
  statusKeys
} from '../../../data';
import { useMemo, useState } from 'react';
import { jurisdictionService } from '../../../../../../services/jurisdiction.service';
import toast from 'react-hot-toast';
import { FaArrowRight } from 'react-icons/fa6';
import { IoMdInformationCircleOutline } from 'react-icons/io';
import { LuMessageCircle } from 'react-icons/lu';
import { RiCheckDoubleFill } from 'react-icons/ri';
import { IoMdTime } from 'react-icons/io';
import { TbChecks, TbClock } from 'react-icons/tb';
import { REPORT_TYPES } from '../../../../../../consts/reportTypes';
import { useCopyApprovers } from '../../../../../../services/automation.service';
import { COMMENT_SERVICE } from '../../../../../../services/comment.service';
import { PermissionGuard } from '../../../../../../components/PermissionGuard';

export const ApprovalColumn = ({
  period,
  workflowId,
  onOpenApprover,
  refetch,
  reportType,
  canManageWorkflow
}) => {
  const [isLoadingCommentThreads, setIsLoadingCommentThreads] = useState(false);

  const status = useMemo(() => {
    if (period.filing_type === 'PF_FINAL') return getStatusForPFFinal(period);
    if (period.filing_type === 'PF_TRANS') return getStatusForPFTrans(period);
    if (reportType === REPORT_TYPES.CONVERSION) return getStatusForConversionReportType(period);
    else if (reportType === REPORT_TYPES.CUSTOM) return getStatusForCustomReportType(period);

    return getStatus(period);
  }, [reportType, period]);

  const placeholder = useMemo(() => {
    if (period.filing_type === 'PF_FINAL') return getPlaceholderForPFFinal(period);
    if (period.filing_type === 'PF_TRANS') return getPlaceholderForPFTrans(period);
    if (reportType === REPORT_TYPES.CONVERSION)
      return getPlaceholderForConversionReportType(period, reportType);
    else if (reportType === REPORT_TYPES.CUSTOM)
      return getPlaceholderForCustomReportType(period, reportType);

    return getPlaceholder(period, reportType);
  }, [reportType, period]);

  // Coalesce nullable schema fields (PeriodCommentsSchema declares both as
  // `number | null`) so template literals like `${resolvedThreads}/${totalThreads}`
  // don't render "null/N Resolved" when the backend omits a count.
  const totalThreads = period?.comments?.total_threads ?? 0;
  const resolvedThreads = period?.comments?.resolved_threads ?? 0;
  const hasComments = totalThreads > 0;

  const copyApprovers = useCopyApprovers();

  const handleOpenApprover = () => {
    if (
      !isFromScratch(period) &&
      period.approve_progress_status === APPROVAL_STATUSES.NOT_STARTED
    ) {
      copyApprovers.mutate(
        {
          period_id: period.id
        },
        {
          onSuccess: () => {
            onOpenApprover();
          }
        }
      );
    } else {
      onOpenApprover();
    }
  };

  const redirectToComments = () => {
    setIsLoadingCommentThreads(true);
    COMMENT_SERVICE.getAllComments({
      workflow_id: workflowId,
      period_id: period.id
    })
      .then(async response => {
        const thread = response?.[0];
        const _reportType = thread?.report_type?.toLowerCase();
        let reportUrl = '/report';

        if (reportType === REPORT_TYPES.FORM_PF) reportUrl += '/pf/us';

        // helper to build query params safely
        const buildQuery = params => {
          const query = new URLSearchParams();

          Object.entries(params).forEach(([key, value]) => {
            if (value) query.append(key, value);
          });

          return query.toString();
        };

        if (reportType === REPORT_TYPES.FORM_PF)
          return window.location.replace(
            `${reportUrl}/${workflowId}/${period?.id}?${buildQuery({
              threadId: thread?.id,
              type: thread?.fund_id ? 'AIF002' : 'AIF001'
            })}`
          );

        if (_reportType === 'fca') {
          const query = buildQuery({
            threadId: thread?.id,
            fund_id: thread?.fund_id,
            type: thread?.fund_id ? undefined : 'AIF001'
          });

          return window.location.replace(
            `${reportUrl}/fca/uk/${workflowId}/${period?.id}?${query}`
          );
        }

        const jurisdictions = await jurisdictionService.getJurisdictions(period?.id);
        const natCode = jurisdictions?.[0]?.reporting_member_state?.toLowerCase();

        const query = buildQuery({
          reportType: period?.is_grouped ? 'grouped' : undefined,
          threadId: thread?.id,
          fund_id: thread?.fund_id,
          type: thread?.fund_id ? undefined : 'AIF001'
        });

        window.location.replace(
          `${reportUrl}/esma/${natCode}/${workflowId}/${period?.id}${query ? `?${query}` : ''}`
        );
      })
      .catch(() => {
        toast.error('Failed to fetch comments.');
      })
      .finally(() => {
        setIsLoadingCommentThreads(false);
      });
  };

  // Only paint "Send For Approval" when approval literally hasn't started.
  // Previously this branch fired on `status === APPROVAL` alone, which was
  // fine while `getStatus` treated 'In progress' as END/SUBMISSION — with
  // the data.jsx fix, APPROVAL is now returned for in-flight approvals too,
  // so the IN_PROGRESS branch below was being shadowed by this one.
  const approveStatus = period?.approve_progress_status;
  const isApprovalUntouched = !approveStatus || approveStatus === APPROVAL_STATUSES.NOT_STARTED;
  if (status === statusKeys.APPROVAL && !hasComments && isApprovalUntouched)
    return (
      <PermissionGuard allowed={canManageWorkflow}>
        <ActionLabel
          onClick={handleOpenApprover}
          label="Send For Approval"
          rightIcon={<FaArrowRight />}
        />
      </PermissionGuard>
    );

  if (period?.approve_progress_status === APPROVAL_STATUSES.IN_PROGRESS && !hasComments)
    return (
      <PermissionGuard allowed={canManageWorkflow}>
        <ActionLabel
          color="#868E96"
          noFill
          leftIcon={<TbClock fontSize="16px" color="#868E96" />}
          onClick={onOpenApprover}
          label="Pending Approval"
          rightIcon={<FaArrowRight fontSize="12px" color="#868E96" />}
          style={{
            whiteSpace: 'nowrap'
          }}
        />
      </PermissionGuard>
    );

  if (period?.approve_progress_status === APPROVAL_STATUSES.COMPLETED && !hasComments)
    return (
      <PermissionGuard allowed={canManageWorkflow}>
        <ActionLabel
          color="#40C057"
          noFill
          leftIcon={<TbChecks fontSize="16px" color="#40C057" />}
          onClick={onOpenApprover}
          label="Approved"
          rightIcon={<FaArrowRight fontSize="12px" color="#40C057" />}
          style={{
            whiteSpace: 'nowrap'
          }}
        />
      </PermissionGuard>
    );

  // Anything that isn't COMPLETED and has no comments — including
  // NOT_STARTED, unknown values, and null/undefined from the API — gets
  // the placeholder pipeline. `placeholder` from getPlaceholder* may itself
  // be null; ActionLabel's scrubber renders an empty cell rather than the
  // word "null".
  if (period?.approve_progress_status !== APPROVAL_STATUSES.COMPLETED && !hasComments)
    return <ActionLabel placeholder={placeholder} rightIcon={<FaArrowRight />} />;

  return (
    <>
      {period?.approve_progress_status === APPROVAL_STATUSES.NOT_STARTED && (
        <PermissionGuard allowed={canManageWorkflow}>
          <ActionLabel
            onClick={handleOpenApprover}
            label="Send For Approval"
            rightIcon={<FaArrowRight />}
          />
        </PermissionGuard>
      )}

      {period?.approve_progress_status === APPROVAL_STATUSES.COMPLETED && (
        <PermissionGuard allowed={canManageWorkflow}>
          <ActionLabel
            onClick={onOpenApprover}
            label="Approved"
            color="#40C057"
            noFill
            rightIcon={<FaArrowRight color="#40C057" />}
            leftIcon={<RiCheckDoubleFill color="#40C057" />}
            style={{
              whiteSpace: 'nowrap'
            }}
          />
        </PermissionGuard>
      )}

      {period?.approve_progress_status === APPROVAL_STATUSES.IN_PROGRESS && (
        <PermissionGuard allowed={canManageWorkflow}>
          <ActionLabel
            onClick={onOpenApprover}
            label="Pending Approval"
            color="#868E96"
            noFill
            rightIcon={<FaArrowRight color="#868E96" />}
            leftIcon={<IoMdTime color="#868E96" />}
            style={{
              whiteSpace: 'nowrap'
            }}
          />
        </PermissionGuard>
      )}

      {hasComments && (
        <ActionLabel
          label={`${resolvedThreads}/${totalThreads} Resolved`}
          color="#868E96"
          noFill
          onClick={redirectToComments}
          leftIcon={<LuMessageCircle color="#868E96" />}
          rightIcon={
            <IoMdInformationCircleOutline
              color="#868E96"
              data-tooltip-id="ligth-tooltip"
              data-tooltip-content="Shows if the comments in this section have been resolved or still need your attention."
            />
          }
        />
      )}

      {hasComments && resolvedThreads === 0 && (
        <ActionLabel
          label={
            isLoadingCommentThreads
              ? 'Loading...'
              : `${totalThreads} New Comment${totalThreads > 1 ? 's' : ''}`
          }
          color="#228BE6"
          noFill
          onClick={redirectToComments}
          leftIcon={<LuMessageCircle color="#228BE6" />}
        />
      )}
    </>
  );
};
